<template>
  <transition name="slide-down">
    <div v-if="shouldShowCallCard" class="call-notification-overlay">
      <div class="call-notification-container">
        <AudioCallCard
          :contact-name="contactName"
          :phone-number="call.currentCallNumber"
          :call-status-text="callStatusText"
          :call-duration-label="callDurationLabel"
          :selected-status="selectedStatus"
          :status-list="statusList"
          :is-end-btn-show="isEndBtnShow"
          :is-redial-btn-show="isRedialBtnShow"
          :is-accept-btn-show="isAcceptBtnShow"
          :is-reject-btn-show="isRejectBtnShow"
          :is-show-evaluation-options="isShowEvaluationOptions"
          :is-show-close-btn="isShowCloseBtn"
          :is-accepting="call.isAccepting"
          :is-rejecting="call.isRejecting"
          :is-redialing="call.isRedialing"
          :call-status="callStatus"
          @onStatusChange="onStatusChange"
          @acceptCall="acceptCall"
          @rejectCall="rejectCall"
          @redialCall="redialCall"
          @endCall="endCall"
          @closeCall="closeCall"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';
import moment from 'moment';
import AudioCallCard from 'components/widgets/conversation/AudioCallCard.vue';
import unifiedCallMixin from 'shared/mixins/unifiedCallMixin.js';
import {
  CALL_DIRECTION,
  CALL_STATUS,
} from 'components/widgets/modal/constants';
import { CALL_TWILIO_EVALUATION } from 'dashboard/routes/dashboard/settings/campaigns/broadcastAgent/aiCampaignConstant.js';
import VoiceAPI from 'dashboard/api/voice.js';

export default {
  components: {
    AudioCallCard,
  },
  mixins: [alertMixin, clickaway, unifiedCallMixin],
  data() {
    return {
      selectedStatus: {
        id: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      call: 'call/getUnifiedCall',
      callUI: 'call/getUnifiedUI',
    }),
    shouldShowCallCard() {
      // Show card if call is active OR if it's in a terminal state awaiting evaluation
      return (
        this.call.callActive ||
        [
          CALL_STATUS.COMPLETED,
          CALL_STATUS.FAILED,
          CALL_STATUS.DISCONNECTED,
        ].includes(this.call.callStatus)
      );
    },
    contactName() {
      return this.call.contact?.name || this.call.contact?.phone_number;
    },
    callStatusText() {
      switch (this.call.callStatus) {
        case CALL_STATUS.DIALING:
        case CALL_STATUS.RINGING:
          return this.call.callDirection === CALL_DIRECTION.OUTGOING
            ? this.$t('CONTACT_PANEL.CALL.CALL_LABEL.DIALING')
            : this.$t('CONTACT_PANEL.CALL.CALL_LABEL.INCOMING_FORM');

        case CALL_STATUS.CONNECTED:
          return this.call.callDirection === CALL_DIRECTION.OUTGOING
            ? this.$t('CONTACT_PANEL.CALL.CALL_LABEL.OUT_TO')
            : this.$t('CONTACT_PANEL.CALL.CALL_LABEL.IN_CALL');

        case CALL_STATUS.DISCONNECTED:
        case CALL_STATUS.COMPLETED:
          return this.call.callDirection === CALL_DIRECTION.OUTGOING
            ? this.$t('CONTACT_PANEL.CALL.CALL_LABEL.END_CALL')
            : this.$t('CONTACT_PANEL.CALL.CALL_LABEL.END_CALL');

        case CALL_STATUS.FAILED:
          return this.call.callDirection === CALL_DIRECTION.OUTGOING
            ? this.$t('CONTACT_PANEL.CALL.CALL_LABEL.ERROR_CALL')
            : this.$t('CONTACT_PANEL.CALL.CALL_LABEL.ERROR_CALL');

        default:
          return '';
      }
    },
    callStatus() {
      const labelObj = {
        label: this.call.callStatus,
        color: 'secondary',
      };
      switch (this.call.callStatus) {
        case CALL_STATUS.DIALING:
        case CALL_STATUS.RINGING:
          labelObj.color = 'secondary';
          break;

        case CALL_STATUS.CONNECTED:
          labelObj.color = 'success';
          break;

        case CALL_STATUS.COMPLETED:
          labelObj.color = 'success';
          break;

        case CALL_STATUS.FAILED:
        case CALL_STATUS.DISCONNECTED:
          labelObj.color = 'alert';
          break;

        default:
          return '';
      }
      return labelObj;
    },
    callDurationLabel() {
      const duration = moment.duration(this.callUI.callDuration, 'seconds'); // convert seconds to duration
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      if (hours > 0) {
        // show HH:MM:SS
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
          2,
          '0'
        )}:${String(seconds).padStart(2, '0')}`;
      }

      // show MM:SS
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0'
      )}`;
    },
    statusList() {
      return CALL_TWILIO_EVALUATION.map(el => ({
        ...el,
        title: this.$t(el.title),
      }));
    },
    isAcceptBtnShow() {
      // Only for incoming calls in dialing state
      return (
        this.call.callDirection === CALL_DIRECTION.INCOMING &&
        [CALL_STATUS.DIALING, CALL_STATUS.RINGING].includes(
          this.call.callStatus
        )
      );
    },
    isRejectBtnShow() {
      // Only for incoming calls in dialing state
      return (
        this.call.callDirection === CALL_DIRECTION.INCOMING &&
        [CALL_STATUS.DIALING, CALL_STATUS.RINGING].includes(
          this.call.callStatus
        )
      );
    },
    isEndBtnShow() {
      // For ongoing calls:
      // - Incoming: connected
      // - Outgoing: connected or dialing
      if (this.call.callDirection === CALL_DIRECTION.INCOMING) {
        return this.call.callStatus === CALL_STATUS.CONNECTED;
      }

      if (this.call.callDirection === CALL_DIRECTION.OUTGOING) {
        return [
          CALL_STATUS.DIALING,
          CALL_STATUS.CONNECTED,
          CALL_STATUS.RINGING,
        ].includes(this.call.callStatus);
      }
      return false;
    },
    isRedialBtnShow() {
      // For completed, failed, or disconnected calls (both incoming and outgoing)
      return [
        CALL_STATUS.COMPLETED,
        CALL_STATUS.FAILED,
        CALL_STATUS.DISCONNECTED,
      ].includes(this.call.callStatus);
    },
    isShowEvaluationOptions() {
      // Only show if call was connected AND is now in a terminal state
      const isTerminalState = [
        CALL_STATUS.COMPLETED,
        CALL_STATUS.FAILED,
        CALL_STATUS.DISCONNECTED,
      ].includes(this.call.callStatus);

      return isTerminalState && this.call.wasConnected;
    },
    isShowCloseBtn() {
      // Show close button for terminal states
      return [
        CALL_STATUS.COMPLETED,
        CALL_STATUS.FAILED,
        CALL_STATUS.DISCONNECTED,
      ].includes(this.call.callStatus);
    },
  },
  watch: {},
  methods: {
    async onStatusChange(event) {
      try {
        const payload = {
          call_id: this.call.currentCallId,
          evaluation: event?.target.value,
          status: this.call.evaluationInfo?.status,
          duration: this.call.evaluationInfo?.duration,
        };

        await VoiceAPI.call_evaluation(payload);

        // clear the selected state
        this.selectedStatus.id = '';
        this.resetState();
      } catch (error) {
        this.selectedStatus.id = '';
        this.showErrAlert(
          error,
          this.$t('CAMPAIGN.CAMPAIGN_FORMS.ADD.ACTIONS.FAIL')
        );
        // clear the selected state
      }
    },
    closeCall() {
      // Reset call state and close the notification
      this.selectedStatus.id = '';
      this.resetState();
    },
  },
};
</script>

<style scoped lang="scss">
.call-notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.call-notification-container {
  margin-top: 16px;
  max-width: 400px;
  width: 90%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  backdrop-filter: blur(10px);
  padding: 16px;
}

// Slide down animation
.slide-down-enter-active {
  animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-leave-active {
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

@keyframes slideDown {
  from {
    transform: translateY(-120%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-120%);
    opacity: 0;
  }
}
</style>
